import React, { useEffect, useReducer, useState } from 'react';
import {
  Box,
  Flex,
  Grid,
  SimpleGrid,
  useBreakpointValue,
} from '@chakra-ui/react';
import { assoc, equals } from 'ramda';
import { ChevronDown32, ChevronRight32 } from '@carbon/icons-react';

import Select, { OnChangeCallbackData } from '#coreComponents/Select';
import { QueryBuilderProps } from './types';
import { reducer, initialState } from './reducer';
import {
  onQuerySelectOneChange,
  onQuerySelectThreeChange,
  onQuerySelectTwoChange,
  onYearChange,
} from './actions';
import Heading from '#coreComponents/Heading';

const QueryBuilder = ({
  config,
  onChange,
  defaultSelectedData,
}: QueryBuilderProps) => {
  const {
    querySelectOne: querySelectOneData,
    variableOptions,
    map,
    filteredOptions,
    years,
    dataOptions,
  } = config;

  const [filters, setFilters] = useState(
    defaultSelectedData?.selectedFilters || {},
  );

  const [state, dispatch] = useReducer(reducer, {
    ...initialState,
    querySelectOne: defaultSelectedData?.querySelectOne || {
      data: querySelectOneData,
      label: querySelectOneData.options[0].label,
      selectedItems: [],
    },
    querySelectTwo:
      defaultSelectedData?.querySelectTwo || initialState.querySelectTwo,
    querySelectThree:
      defaultSelectedData?.querySelectThree || initialState.querySelectThree,
    selectedYears: defaultSelectedData?.selectedYears || [],
    variableOptions,
    filteredOptions,
    selectedFilters: filters,
    map,
  });

  const {
    querySelectOne,
    querySelectTwo,
    querySelectThree,
    filterSelectOptions,
    selectedYears,
  } = state;

  const isMobile = useBreakpointValue({ base: true, md: false });

  useEffect(() => {
    const sanitisedFilters = Object.entries(filters).reduce(
      (acc, [key, val]) => {
        if (!val) {
          return acc;
        }

        return {
          ...acc,
          [key]: val,
        };
      },
      {},
    );

    const indicatorData = state.querySelectOne.data.options.find(
      (item) => item.label === state.querySelectOne.label,
    )!;

    const mapItem = map.find((i) => i.key === indicatorData.key)!;

    const selectedChildren =
      indicatorData.children &&
      indicatorData.children.options.filter((option) =>
        state.querySelectOne.selectedItems.includes(option.key),
      );

    const indicator = {
      key: mapItem.key,
      type: mapItem.type,
      options: mapItem.options,
      querySelectThree: mapItem.querySelectThree,
      filteredOptions: mapItem.filteredOptions,
      gql: mapItem.gql,
      label: state.querySelectOne.label,
      selectedChildren: selectedChildren?.filter(Boolean),
      selectedFilters: Object.entries(filteredOptions)
        .filter(([, values]) => values.meta?.selected)
        .reduce(
          (acc, [key, values]) => ({ ...acc, [key]: values.meta?.selected }),
          {},
        ),
    };

    const [dimension] = state.querySelectTwo.selectedItems.map((key) => ({
      label: state.querySelectTwo.label,
      key,
    }));

    const [groupBy] = state.querySelectThree.selectedItems.map((key) => ({
      label: state.querySelectThree.label,
      key,
      dataOptions: dataOptions[key],
    }));

    onChange({
      indicator,
      dimension,
      groupBy,
      years: state.selectedYears,
      filters: sanitisedFilters,
      groupByKey: groupBy?.key,
      indicatorDataKey: indicatorData.key,
    });
  }, [map, dataOptions, state, filters, filteredOptions, onChange]);

  const handleYearChange = (selectedData: OnChangeCallbackData[]) => {
    if (selectedData.length) {
      const selectedDataYears = selectedData[0].selectedItems.map((year) =>
        parseInt(year, 10),
      );
      if (!equals(selectedDataYears, selectedYears)) {
        dispatch(onYearChange(selectedDataYears));
      }
    }
  };

  const handleQuerySelectOneChange = (selectedData: OnChangeCallbackData[]) => {
    setFilters({});
    dispatch(onQuerySelectOneChange(selectedData));
  };

  const handleQuerySelectTwoChange = (selectedData: OnChangeCallbackData[]) => {
    dispatch(onQuerySelectTwoChange(selectedData));
  };

  const handleQuerySelectThreeChange = (
    selectedData: OnChangeCallbackData[],
  ) => {
    dispatch(onQuerySelectThreeChange(selectedData));
  };

  const handleFiltersChange = (key?: string) => (
    selectedData: OnChangeCallbackData[],
  ) => {
    setFilters((prevState) =>
      key ? assoc(key, selectedData[0]?.selectedItems, prevState) : prevState,
    );
  };

  return (
    <Flex flexDirection="column" justify="center">
      <Grid
        templateColumns={{ base: '1fr', md: '1fr 40px 1fr 40px 1fr' }}
        alignItems="end"
      >
        <Box>
          <Heading as="h6" mb="1">
            Indicator
          </Heading>
          <Select
            data={querySelectOne.data}
            forceGroupSelections
            label={querySelectOne.label}
            onChange={handleQuerySelectOneChange}
            dark
          />
        </Box>
        <Flex
          pb={{ base: 0, md: 1 }}
          pt={{ base: 1, md: 0 }}
          justifyContent="center"
        >
          {isMobile ? <ChevronDown32 /> : <ChevronRight32 />}
        </Flex>
        <Box>
          <Heading as="h6" mb="1">
            Dimension
          </Heading>
          <Select
            data={querySelectTwo.data}
            forceGroupSelections
            label={querySelectTwo.label}
            onChange={handleQuerySelectTwoChange}
            dark
          />
        </Box>
        <Flex
          pb={{ base: 0, md: 1 }}
          pt={{ base: 1, md: 0 }}
          justifyContent="center"
        >
          {isMobile ? <ChevronDown32 /> : <ChevronRight32 />}
        </Flex>
        <Box>
          <Heading as="h6" mb="1">
            Group by
          </Heading>
          <Select
            data={querySelectThree.data}
            forceGroupSelections
            label={querySelectThree.label}
            onChange={handleQuerySelectThreeChange}
            dark
          />
        </Box>
      </Grid>
      <Box mt="10">
        <Heading as="h6" mb="4">
          Filter by
        </Heading>
        <SimpleGrid
          columns={{ base: 1, md: 3 }}
          justifyContent="center"
          columnGap={10}
          rowGap={4}
        >
          <Box
            gridColumnEnd={{
              md:
                filterSelectOptions && filterSelectOptions.length % 3
                  ? ''
                  : 'span 3',
            }}
          >
            <Heading as="h6" mb="1">
              Years
            </Heading>
            <Select
              data={{
                options: years.map((year) => ({
                  label: year.toString(),
                  key: year.toString(),
                })),
                meta: {
                  type: 'checkbox',
                  selected: selectedYears.map((year) => year.toString()),
                },
              }}
              label={'Years'}
              onChange={handleYearChange}
              dark
            />
          </Box>
          {filterSelectOptions?.map((option) => (
            <Box key={option.key}>
              <Heading as="h6" mb="1">
                {option.label || ''}
              </Heading>
              <Select
                data={option}
                forceGroupSelections
                label={option.label}
                onChange={handleFiltersChange(option.key)}
                dark
              />
            </Box>
          ))}
        </SimpleGrid>
      </Box>
    </Flex>
  );
};

export default QueryBuilder;
